<template>
    <form class="rf-view login-view" @submit.prevent="next">
        <NavigationBar :title="$t('onboarding.company.title')">
            <BackButton v-if="canPop" slot="left" @click="pop" />
        </NavigationBar>
        <main>
            <h1>
                {{ $t('onboarding.company.title') }}
            </h1>

            <GeneralErrorsView :error-box="errorBox" />

            <InputBox :title="$t('fields.businessName')" :error-box="errorBox" error-fields="businessName">
                <input v-model="businessName" class="input" :placeholder="$t('fields.placeholders.businessName')" autocomplete="organization">
            </InputBox>

            <InputBox :title="$t('fields.city')" :error-box="errorBox" error-fields="city">
                <input v-model="city" class="input" :placeholder="$t('fields.placeholders.city')">
            </InputBox>

            <InputBox :title="$t('fields.postalCode')" :error-box="errorBox" error-fields="postalCode">
                <input v-model="postalCode" class="input" :placeholder="$t('fields.placeholders.postalCode')">
            </InputBox>

            <InputBox :title="$t('fields.address')" :error-box="errorBox" error-fields="address">
                <input v-model="address" class="input" :placeholder="$t('fields.placeholders.address')">
            </InputBox>

            <InputBox :title="$t('fields.VATNumber')" :error-box="errorBox" error-fields="VATNumber">
                <input v-model="VATNumber" class="input" :placeholder="$t('fields.placeholders.optional')">
            </InputBox>
        </main>

        <FloatingFooter>
            <LoadingButton :loading="loading" class="block">
                <button class="button primary full">
                    {{ $t('buttons.next') }}
                </button>
            </LoadingButton>
        </FloatingFooter>
    </form>
</template>


<script lang="ts">
import { BackButton,FloatingFooter, InputBox, LoadingButton, NavigationBar } from "@restofrit/components"
import { CreateCustomer, Customer } from "@restofrit/structures";
import { ErrorBox, GeneralErrorsView } from "@simonbackx/simple-error-forms";
import { SimpleError, SimpleErrors } from "@simonbackx/simple-errors";
import { ComponentWithProperties, NavigationMixin } from "@simonbackx/vue-app-navigation";
import { checkVAT, countries } from 'jsvat-next';
import { Component, Mixins, Prop } from "vue-property-decorator";

import CategorySelectionView from "./CategorySelectionView.vue";

@Component({
    components: {
        InputBox,
        GeneralErrorsView,
        LoadingButton,
        FloatingFooter,
        NavigationBar,
        BackButton
    },
})
export default class CompanySettingsView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    customer: CreateCustomer

    @Prop({ required: true })
    callback: (newCustomer: Customer) => void

    errorBox: ErrorBox | null = null
    loading = false

    get businessName() {
        return this.customer.settings.businessName
    }

    set businessName(businessName: string) {
        this.customer.settings.businessName = businessName
    }

    get city() {
        return this.customer.settings.city
    }

    set city(city: string) {
        this.customer.settings.city = city
    }

    get postalCode() {
        return this.customer.settings.postalCode
    }

    set postalCode(postalCode: string) {
        this.customer.settings.postalCode = postalCode
    }

    get address() {
        return this.customer.settings.address
    }

    set address(address: string) {
        this.customer.settings.address = address
    }

    get VATNumber() {
        return this.customer.settings.VATNumber
    }

    set VATNumber(name: string) {
        this.customer.settings.VATNumber = name
    }

    next() {
        try {
            this.errorBox = null

            this.businessName = this.businessName.trim()
            this.VATNumber = this.VATNumber.trim()

            const errors = new SimpleErrors()
            if (this.businessName.length == 0) {
                errors.addError(new SimpleError({
                    code: "invalid_field",
                    message: "businessName is too short",
                    human: this.$t('errors.emptyBusinessName').toString(),
                    field: "businessName"
                }))
            }

            if (this.city.length == 0) {
                errors.addError(new SimpleError({
                    code: "invalid_field",
                    message: "city is too short",
                    human: this.$t('errors.emptyCity').toString(),
                    field: "city"
                }))
            }

            if (this.postalCode.length == 0) {
                errors.addError(new SimpleError({
                    code: "invalid_field",
                    message: "postalCode is too short",
                    human: this.$t('errors.emptyPostalCode').toString(),
                    field: "postalCode"
                }))
            }

            if (this.address.length == 0) {
                errors.addError(new SimpleError({
                    code: "invalid_field",
                    message: "address is too short",
                    human: this.$t('errors.emptyAddress').toString(),
                    field: "address"
                }))
            }

            if (this.VATNumber.length > 0 && !checkVAT(this.VATNumber, countries).isValid) {
                errors.addError(new SimpleError({
                    code: "invalid_field",
                    message: "VATNumber is too short",
                    human: this.$t('errors.invalidVATNumber').toString(),
                    field: "VATNumber"
                }))
            }

            errors.throwIfNotEmpty()
            this.show(new ComponentWithProperties(CategorySelectionView, { customer: this.customer, callback: this.callback }))
        } catch (e) {
            this.errorBox = new ErrorBox(e)
        }
    }   
}
</script>